import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserInvitationStatus } from '@/hooks/useUserInvitationStatus';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ExistingUserInvitationHandlerProps {
  invitationId: string;
  invitationEmail: string;
  companyName: string;
  onAcceptComplete?: () => void;
}

const ExistingUserInvitationHandler = ({ 
  invitationId, 
  invitationEmail, 
  companyName,
  onAcceptComplete 
}: ExistingUserInvitationHandlerProps) => {
  const { user } = useAuth();
  const { acceptInvitation, isLoading } = useUserInvitationStatus();
  const navigate = useNavigate();

  const handleAcceptInvitation = async () => {
    try {
      await acceptInvitation(invitationId, { skipReload: true });
      
      if (onAcceptComplete) {
        onAcceptComplete();
      }
      // Note: No default navigation to dashboard - stay in join flow until profile is complete
    } catch (error) {
      console.error('Failed to accept invitation:', error);
    }
  };

  if (!user || user.email?.toLowerCase() !== invitationEmail.toLowerCase()) {
    return null;
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>You're Already Signed In!</CardTitle>
        <CardDescription>
          We found your account. You can accept this invitation directly.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 border rounded-lg bg-muted/50">
          <h3 className="font-medium text-sm text-muted-foreground">Invitation to Join</h3>
          <p className="font-semibold">{companyName}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Invited: {invitationEmail}
          </p>
        </div>
        
        <Button 
          onClick={handleAcceptInvitation}
          disabled={isLoading}
          className="w-full"
          size="lg"
        >
          {isLoading ? 'Accepting Invitation...' : 'Accept Invitation & Join Company'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ExistingUserInvitationHandler;
